import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import FooterV2 from '../components/landing/v2/FooterV2';
import Navigation from '../components/Navigation';
import BackLink from '../components/BackLink';
import MarketingHeader from '../components/MarketingHeader';
import { Crest, brandBand } from '../components/UniversityCrest';
import { CycleNote, FactSection, FactTile, SourceNote, TableYear } from '../components/colleges/CollegeParts';
import MajorsSection from '../components/colleges/MajorsSection';
import SimilarSchools from '../components/colleges/SimilarSchools';
import CampusPhoto, { PHOTO_SIZES, photoSrcSet } from '../components/colleges/CampusPhoto';
import {
    SCORECARD_NET_PRICE_YEAR, acceptanceRate, collegeFaqJsonLd, collegeJsonLd, count, cycleLine, cycleOf, getCollegeBySlug, pct, testingTileDetail, usd,
} from '../data/colleges';
import { formatCalendarDate } from '../utils/calendarDate';
import { useAuth } from '../context/AuthContext';
import { useSidebar, sidebarContentPad } from '../context/SidebarContext';
import { track, EVENTS } from '../services/analytics';
import usePageMeta from '../utils/usePageMeta';
import { collegeDescription, collegeTitle } from '../seo/collegeMeta';
import { collegeOddsLink } from '../utils/schoolEntry';
import { ASTRA_HOME, cameFromAstra } from '../services/astraChatThread';
import AskAstraBox from '../components/colleges/AskAstraBox';
import StandCard from '../components/colleges/StandCard';
import { collegeSeeds } from '../content/collegeAsk';
import { ASK_HASH } from '../utils/askLink';
import useAskAstraOn from '../hooks/useAskAstraOn';
import useAdLanding, { usePaidLanding } from '../hooks/useAdLanding';
import AdLandingBlock from '../components/colleges/AdLandingBlock';
import { landingsFor } from '../content/adLandings';
import useGoogleSignIn from '../hooks/useGoogleSignIn';
import SigningInOverlay from '../components/SigningInOverlay';
import CollegeNews from '../components/colleges/CollegeNews';
import CollegeVideos from '../components/colleges/CollegeVideos';
import useCollegeVideos from '../hooks/useCollegeVideos';
import { videoJsonLd } from '../services/collegeVideos';
import collegeNewsIndex from '../data/collegeNews.json';
import { collegeStories } from '../utils/collegeNews';
import { classYear } from '../utils/newsFind';
import { fetchStructuredProfile } from '../services/api';
import lazyWithRetry from '../utils/lazyWithRetry';
import useSignupMoments from '../hooks/useSignupMoments';
import LongReadBlock from '../components/signup/LongReadBlock';
import ComparingStrip from '../components/signup/ComparingStrip';
import { COPY as MOMENT_COPY, MOMENTS } from '../content/signupMoments';
import { holdMoment, noteCollegeVisit } from '../services/signupMoments';
import { storeCarriedSchools } from '../utils/schoolEntry';

// The Astra sheet (#3262) loads only when first opened.
const AstraSheet = lazyWithRetry(() => import('../components/colleges/AstraSheet'), 'AstraSheet');

// /colleges/<slug> (#2579, founder: "YES to public college pages with
// official facts only"; template approved 2026-09-30, batch 1 #2596). What a
// family can check: the first-year rate and its cycle (by residency where the
// school reports it), rates by school or major where published, programs with
// extra requirements, deadlines, test policy, cost and net price, each beside
// its source and read date. Never a fit score, anyone's odds, a
// profile-derived line or a self-reported record: those live in the app.
// The one exception is the visitor's own: the "Where do I stand" card (#3885)
// shows Astra's read only after they ask for it, never prerendered.
// The data shape is documented in data/colleges/index.js.

function Table({ head, rows, foot }) {
    return (
        <div className="overflow-x-auto rounded-xl border border-[#E0DED8] bg-white">
            <table className="w-full text-left text-sm sm:text-[15px]">
                {head && (
                    <thead>
                        <tr className="text-[11px] uppercase tracking-[0.08em] text-[#6B6B6B]">
                            {head.map((h, i) => (
                                <th key={h} scope="col" className={`px-2.5 py-2 sm:px-4 font-bold ${i ? 'text-right' : ''}`}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {rows.map((r) => (
                        <tr key={r[0]} className="border-t border-[#EFEDE7] first:border-t-0">
                            <th scope="row" className="px-2.5 py-2 sm:px-4 font-normal">{r[0]}</th>
                            {r.slice(1).map((v, i) => <td key={i} className="px-2.5 py-2 sm:px-4 text-right tabular-nums">{v}</td>)}
                        </tr>
                    ))}
                    {foot && (
                        <tr className="border-t border-[#E0DED8] font-semibold text-[#1A2E1F]">
                            <th scope="row" className="px-2.5 py-2 sm:px-4">{foot[0]}</th>
                            {foot.slice(1).map((v, i) => <td key={i} className="px-2.5 py-2 sm:px-4 text-right tabular-nums">{v}</td>)}
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

const money = (v) => (v === null || v === undefined ? 'not published' : usd(v));

const CollegePage = () => {
    const { slug } = useParams();
    const c = getCollegeBySlug(slug);
    const { currentUser } = useAuth();
    const { collapsed } = useSidebar();
    const rate = c ? acceptanceRate(c) : null;
    // Opened from an Astra answer's chip (#2809): the way back is the chat,
    // not the index. Read after mount, so the prerendered page (no router
    // state) hydrates exactly as built; the URL and canonical never change.
    const { state, hash, search } = useLocation();
    // A paid landing (#4088): seen on load, stayed after 10 seconds.
    useAdLanding(c ? c.slug : null, c ? c.kbId : null, search);
    // The paid first screen (#4476, founder pick A): every answer the page can
    // give rides a JSON data tag, built into the header only on a paid visit
    // (AdLandingBlock, #4492). `landing` tags the stand card's events.
    const landings = useMemo(() => landingsFor(c), [c]);
    const landing = usePaidLanding(c, search);
    const [fromAstra, setFromAstra] = useState(false);
    useEffect(() => { setFromAstra(cameFromAstra(state)); }, [state]);

    // Ask Astra about this school (#3262, founder pick A of the Astra entry
    // mocks, 2026-10-06): the box under the fact tiles, and the sheet it opens.
    // The box shows only where the open chat answers this visitor, as on the
    // landing page: a guest, or an account dogfooding it (the preview token or
    // the astra_lite allowlist); the route's own status says it is open
    // (hooks/useAskAstraOn, shared with the News box).
    const { signIn, phase: signInPhase } = useGoogleSignIn();
    const [sheet, setSheet] = useState({ open: false, mounted: false, pending: null, scope: 'college_page' });
    const [askAvail, closeAsk] = useAskAstraOn(Boolean(c && c.kbId));
    const askOn = Boolean(c) && Boolean(c.kbId) && askAvail;
    const askSchool = useMemo(() => (c ? {
        id: c.kbId, slug: c.slug, name: c.name, shortName: c.shortName || c.name,
        testBlind: Boolean(c.testing && c.testing.blind), aliases: c.aliases || [],
    } : null), [c]);
    const seeds = useMemo(() => (c ? collegeSeeds(c) : []), [c]);
    const openSheet = useCallback((ask, via) => {
        track(EVENTS.ASTRA_SHEET_OPENED, { slug: c.slug, school_id: c.kbId || null, via });
        setSheet((sh) => ({
            open: true, mounted: true,
            scope: via === 'ask_link' ? 'ask_link' : sh.scope,
            pending: ask ? { q: ask.q, entry: ask.entry, seedId: ask.seedId || null, n: Date.now() } : sh.pending,
        }));
    }, [c]);
    const closeSheet = useCallback(() => {
        setSheet((sh) => ({ ...sh, open: false }));
        if (typeof window !== 'undefined' && window.location.hash === `#${ASK_HASH}`) {
            try { window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search); } catch { /* old browsers */ }
        }
    }, []);
    // /ask/<short> lands here with #ask (#3262, pick 1b-A): the sheet opens on
    // the school's seeds, asking nothing until the visitor picks or types.
    useEffect(() => {
        if (askOn && hash === `#${ASK_HASH}` && !sheet.open) openSheet(null, state && state.askLink ? 'ask_link' : 'hash');
    }, [askOn, hash]);   // eslint-disable-line react-hooks/exhaustive-deps
    // An in-app link to a section (#3980: the Strategy page's "All majors at
    // <School>" goes to #majors) lands on it. A full load of the prerendered
    // page scrolls by itself; a client-side navigation does not, so the page
    // scrolls once it has rendered. #ask is the Astra sheet's, above.
    useEffect(() => {
        const id = (hash || '').slice(1);
        if (!id || id === ASK_HASH || typeof document === 'undefined') return undefined;
        const t = setTimeout(() => {
            let el = null;
            try { el = document.getElementById(decodeURIComponent(id)); } catch { el = null; }
            if (typeof el?.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
        }, 0);
        return () => clearTimeout(t);
    }, [hash, slug]);

    // Sign-up moments (#5195, signup_moments, founder pick A): the long-read
    // block after Deadlines, the comparing strip on the second college page
    // of a visit, and the bottom prompt opening this page's card instead of
    // leaving. Signed-out visitors only, where the card is offered; nothing
    // here is in the prerendered page (each appears after the visitor acts or
    // after mount), so off is today's page exactly.
    const momentsOn = useSignupMoments();
    const momentsHere = momentsOn && Boolean(c && c.kbId) && askOn && !currentUser;
    const [standVia, setStandVia] = useState(null);
    const [standUsed, setStandUsed] = useState(false);
    const [visit, setVisit] = useState(null);
    useEffect(() => {
        setStandVia(null);
        setStandUsed(false);
        setVisit(momentsHere ? noteCollegeVisit({ slug: c.slug, id: c.kbId, short: c.shortName || c.name }) : null);
    }, [momentsHere, slug]);   // eslint-disable-line react-hooks/exhaustive-deps
    // Open the page's one card in place: scroll to it and put focus on its
    // first field (one card, never a second form). `via`: the moment that
    // opened it, so its box counts as that moment's.
    const openStand = useCallback((via) => {
        setStandVia(via);
        const card = document.querySelector('[data-testid="college-stand"]');
        const still = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
        card?.scrollIntoView?.({ behavior: still ? 'auto' : 'smooth', block: 'center' });
        document.getElementById('college-stand-grade')?.focus({ preventScroll: true });
    }, []);
    const saveVisit = useCallback(() => {
        if (!visit) return;
        const ids = visit.colleges.map((x) => x.id);
        // offered on /start (the try-hero carry key /start reads), never added
        storeCarriedSchools(visit.colleges.map((x) => ({ university_id: x.id, name: x.short })), null);
        holdMoment({ moment: 'comparing', schoolIds: ids, gpa: visit.gpa || null,
            targets: Boolean(visit.targetsN) });
        const box = MOMENTS.comparing.box({ schools: visit.colleges, gpa: visit.gpa || null,
            targets: Number.isInteger(visit.targetsN) ? { n: visit.targetsN } : null });
        signIn(MOMENTS.comparing.entry, box ? { title: box.button, lead: box.lead } : null);
    }, [visit, signIn]);

    // In the news (#3972, founder pick B): this school's tagged stories. A
    // signed-in student's class is read after mount, and only on a page that
    // has a story, so the prerendered page hydrates as built and a page with
    // no story costs no profile read. A failed read keeps the visitor's line.
    const news = collegeStories(collegeNewsIndex, c?.kbId);
    const hasNews = news.length > 0;
    const email = currentUser?.email;
    const [cls, setCls] = useState(null);
    useEffect(() => {
        setCls(null);
        if (!email || !hasNews) return undefined;
        let alive = true;
        fetchStructuredProfile(email)
            .then((res) => { if (alive && res?.success) setCls(classYear(res.profile)); })
            .catch(() => {});
        return () => { alive = false; };
    }, [email, hasNews]);

    // Watch: our videos about this school (#4608, founder pick B), before
    // Similar schools; the YouTube ones also go in the head as VideoObject.
    const videos = useCollegeVideos(c ? c.slug : null);

    usePageMeta({
        // The search head (#3251): the rate in the title only where the KB holds it.
        title: c ? collegeTitle(c) : undefined,
        description: c ? collegeDescription(c) : undefined,
        path: c ? `/colleges/${c.slug}` : undefined,
        jsonLd: c ? [collegeJsonLd(c), collegeFaqJsonLd(c), ...videoJsonLd(videos)].filter(Boolean) : null,
        preloadImage: c && c.photo ? { src: c.photo.src, srcSet: photoSrcSet(c.photo), sizes: PHOTO_SIZES } : null,
        image: c && c.photo ? { url: c.photo.og, width: 1200, height: 630, alt: `${c.name}: official facts, sourced` } : null,
    });

    if (!c) return <Navigate to="/colleges" replace />;
    const { sources, admission, byMajor, earlyRounds, programs, deadlines, testing, cost, nextCycle } = c;
    const next = nextCycle ? cycleOf(nextCycle.entering) : null;
    const cols = cost.columns || null;
    const costRow = (r) => (cols ? [r.item, ...r.amounts.map(money)] : [r.item, money(r.amount)]);

    return (
        <div className={`min-h-screen bg-[#FDFCF7] ${currentUser ? `${sidebarContentPad(collapsed)} transition-[padding] duration-300` : ''}`}>
            {currentUser ? <Navigation /> : <MarketingHeader />}
            <SigningInOverlay phase={signInPhase} />
            <header className={`relative border-b border-[#E0DED8] ${currentUser ? '' : 'pt-16'}`}>
                <div aria-hidden="true" className="h-1.5 w-full" style={{ background: brandBand(c.branding) }} />
                <div className="college-head w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8">
                    {fromAstra ? (
                        <Link to={ASTRA_HOME} data-testid="college-back-to-astra" onClick={() => track(EVENTS.ASTRA_CHAT_BACK, {})}
                            className="ch-back inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-[#CFE0D2] bg-white px-3.5 text-sm font-semibold text-[#1A4D2E] hover:bg-[#F0F7F1]">
                            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Back to Astra
                        </Link>
                    ) : (
                        <BackLink fallbackTo="/colleges" fallbackLabel="All colleges" testId="college-back"
                            className="ch-back inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-[#6B6B6B] hover:text-[#1A4D2E]" />
                    )}
                    <div className="ch-name mt-2 flex items-center gap-4">
                        <Crest name={c.name} branding={c.branding} size={64} />
                        <div className="min-w-0">
                            <h1 className="font-serif text-3xl sm:text-5xl font-bold leading-tight text-[#1A2E1F]">{c.name}</h1>
                            <p className="mt-1 text-sm text-[#4A4A4A]">{c.location} · {c.control}</p>
                        </div>
                    </div>
                    {momentsHere && visit && visit.colleges.length > 1 && (
                        <ComparingStrip visit={visit} current={c.slug} onSave={saveVisit}
                            onCheck={() => openStand(null)} className="mt-3" />
                    )}
                    <AdLandingBlock college={c} landings={landings} landing={landing} />
                    {/* #4090: the one-sentence answer to the search that lands here */}
                    {c.faq && c.faq.answer && (
                        <div data-testid="college-answer" className="ch-answer mt-4 max-w-3xl">
                            <p className="text-lg sm:text-xl leading-relaxed text-[#1A2E1F]">{c.faq.answer.text}</p>
                            <SourceNote sources={sources} sourceKeyName={c.faq.answer.source} where={c.faq.answer.where} className="mt-1" />
                        </div>
                    )}
                    {c.photo && <CampusPhoto photo={c.photo} className="ch-photo" />}
                    <p className="ch-intro mt-5 max-w-3xl text-base sm:text-lg leading-relaxed text-[#4A4A4A]">
                        Official facts only. Every number below links to {c.shortName} or the U.S. Department of Education,
                        with the date we read it. Last checked {formatCalendarDate(c.lastChecked, 'long')}.
                    </p>
                    <div className="ch-tiles mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <FactTile label="Acceptance rate" value={`${rate}%`} detail={`First-year applicants, ${admission.entering}`} />
                        <FactTile label={deadlines.headline.label} value={deadlines.headline.value} detail={`For ${deadlines.entering} entry`} />
                        <FactTile label="Testing" value={testing.policy} detail={testingTileDetail(testing)} />
                        <FactTile label="Average net price" value={usd(cost.netPrice.average)} detail={`${cost.netPrice.tileDetail}, ${SCORECARD_NET_PRICE_YEAR}`} />
                    </div>
                    {/* #3885 (founder pick A, 2026-10-11): a guest's own read, under the tiles */}
                    {c.kbId && (
                        // keyed by school: the route does not remount between college
                        // pages (Similar schools links), and one school's read must
                        // never sit on another's card (review of #3885)
                        <StandCard key={c.slug} college={c} available={askOn && !currentUser} onSignIn={signIn}
                            onUnavailable={() => { closeAsk(); closeSheet(); }} landing={landing} className="ch-stand"
                            moments={momentsHere} via={standVia} onChecked={() => setStandUsed(true)} />
                    )}
                    {c.kbId && (
                        <AskAstraBox college={c} seeds={seeds} available={askOn} className="ch-ask"
                            onOpen={(ask) => openSheet(ask, ask.entry === 'seed' ? 'seed' : 'typed')} />
                    )}
                </div>
            </header>

            <main className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <CollegeNews stories={news} cls={cls} />

                <FactSection id="getting-in" title="Getting in">
                    <p>
                        For {admission.entering}, {count(admission.applied)} first-year students applied
                        to {c.shortName}, {count(admission.admitted)} were admitted
                        {admission.enrolled ? <> and {count(admission.enrolled)} enrolled</> : null}:
                        an acceptance rate of <strong className="text-[#1A2E1F]">{rate}%</strong>.
                    </p>
                    <SourceNote sources={sources} sourceKeyName={admission.source} where={admission.where} />
                    {admission.residency && (
                        <>
                            <TableYear>By residency, first-year applicants, {admission.residencyEntering || admission.entering}</TableYear>
                            <Table
                                head={['Applicants', 'Applied', 'Admitted', 'Rate']}
                                rows={admission.residency.map((r) => [r.group, count(r.applied), count(r.admitted), `${pct(r.admitted, r.applied)}%`])}
                            />
                            <SourceNote sources={sources} sourceKeyName={admission.residencySource || admission.source} where={admission.residencyWhere} />
                        </>
                    )}
                    <p><strong className="text-[#1A2E1F]">By school or major.</strong> {byMajor.note}</p>
                    {byMajor.rows && byMajor.rows.length > 0 && (
                        <>
                        <TableYear>Rates by school or major, {byMajor.year}</TableYear>
                        <Table
                            head={byMajor.head || ['School or major', 'Rate']}
                            rows={byMajor.rows.map((r) => [r.name, ...(r.applied ? [count(r.applied), count(r.admitted)] : []), `${r.rate}%`])}
                        />
                        </>
                    )}
                    <SourceNote sources={sources} sourceKeyName={byMajor.source} where={byMajor.where} />
                    {earlyRounds && (
                        <>
                            <p><strong className="text-[#1A2E1F]">{earlyRounds.title || 'Early rounds'}.</strong> {earlyRounds.note}</p>
                            {earlyRounds.rows && earlyRounds.rows.length > 0 && (
                                <>
                                <TableYear>{earlyRounds.title || 'Early rounds'}, {earlyRounds.year}</TableYear>
                                <Table
                                    head={['Plan', 'Applied', 'Admitted', 'Rate']}
                                    rows={earlyRounds.rows.map((r) => [r.plan, count(r.applied), count(r.admitted), `${pct(r.admitted, r.applied)}%`])}
                                />
                                </>
                            )}
                            <SourceNote sources={sources} sourceKeyName={earlyRounds.source} where={earlyRounds.where} />
                        </>
                    )}
                </FactSection>

                {programs && (
                    <FactSection id="programs" title="Programs with extra requirements">
                        <CycleNote line={cycleLine(programs.entering)} testId="cycle-programs" />
                        <p>{programs.intro}</p>
                        <ul className="space-y-2">
                            {programs.rows.map((r) => (
                                <li key={r.school} className="rounded-xl border border-[#E0DED8] bg-white px-4 py-3">
                                    <span className="font-semibold text-[#1A2E1F]">{r.school}.</span> {r.requirement}
                                    {r.source && <SourceNote sources={sources} sourceKeyName={r.source} className="mt-1" />}
                                </li>
                            ))}
                        </ul>
                        {programs.note && <p>{programs.note}</p>}
                        <SourceNote sources={sources} sourceKeyName={programs.source} />
                    </FactSection>
                )}

                {c.majors && <MajorsSection college={c} />}

                <FactSection id="deadlines" title={`Deadlines for ${deadlines.entering}`}>
                    <CycleNote line={cycleLine(deadlines.entering)} testId="cycle-deadlines" />
                    <Table
                        head={['Plan', 'Apply by', 'Decision', 'Binding']}
                        rows={deadlines.rows.map((r) => [r.plan, r.deadline, r.decision || 'not published', r.binding ? 'Yes' : 'No'])}
                    />
                    {deadlines.note && <p>{deadlines.note}</p>}
                    <SourceNote sources={sources} sourceKeyName={deadlines.source} />
                    {deadlines.binding && (
                        <>
                            <p>{deadlines.binding}</p>
                            <SourceNote sources={sources} sourceKeyName={deadlines.bindingSource || deadlines.source} />
                        </>
                    )}
                </FactSection>

                {momentsHere && (
                    <LongReadBlock active={!standUsed}
                        line={MOMENT_COPY.longRead(c.shortName, count(admission.admitted), count(admission.applied), admission.entering)}
                        onCheck={() => openStand('long_read')} />
                )}
                <FactSection id="testing" title="Testing">
                    <CycleNote line={cycleLine(testing.entering)} testId="cycle-testing" />
                    <p>{testing.line}</p>
                    <SourceNote sources={sources} sourceKeyName={testing.source} />
                    {testing.cdsLine && (
                        <>
                            <p>{testing.cdsLine}</p>
                            <SourceNote sources={sources} sourceKeyName="cds" where={testing.cdsWhere} />
                        </>
                    )}
                </FactSection>

                {c.currentChanges && c.currentChanges.length > 0 && (
                    <FactSection id="changes-now" title="Changes already in effect">
                        <CycleNote line={cycleLine(deadlines.entering)} testId="cycle-changes-now" />
                        <ul className="space-y-2" data-testid="current-changes">
                            {c.currentChanges.map((ch) => (
                                <li key={ch.line} className="rounded-xl border border-[#E0DED8] bg-white px-4 py-3">
                                    <span className="font-semibold text-[#1A2E1F]">{ch.topic}.</span> {ch.line}
                                    <SourceNote sources={sources} sourceKeyName={ch.source} className="mt-1" />
                                </li>
                            ))}
                        </ul>
                    </FactSection>
                )}

                {nextCycle && (
                    <FactSection id="next-cycle" title="Applying next year?">
                        <CycleNote line={cycleLine(nextCycle.entering)} testId="cycle-next" />
                        {nextCycle.changes.length > 0 ? (
                            <>
                                <ul className="space-y-2" data-testid="next-cycle-changes">
                                    {nextCycle.changes.map((ch) => (
                                        <li key={ch.line} className="rounded-xl border border-[#E0DED8] bg-white px-4 py-3">
                                            <span className="font-semibold text-[#1A2E1F]">{ch.topic}.</span> {ch.line}
                                            <SourceNote sources={sources} sourceKeyName={ch.source} className="mt-1" />
                                        </li>
                                    ))}
                                </ul>
                                <p>We found nothing else announced for {next.entering} entry. The deadlines and rules above are for {deadlines.entering.toLowerCase()} entry.</p>
                            </>
                        ) : (
                            <p data-testid="next-cycle-none">
                                Not announced yet. When we checked {c.shortName}'s admission pages on {formatCalendarDate(nextCycle.checked, 'long')}, we
                                found no announced changes for {next.entering} entry. The deadlines and rules above are for {deadlines.entering.toLowerCase()} entry.
                            </p>
                        )}
                        <SourceNote sources={sources} sourceKeyName={nextCycle.source} />
                    </FactSection>
                )}

                <FactSection id="cost" title="Cost, and what families paid after aid">
                    <p>The published cost of a year at {c.shortName}, {cost.year}:</p>
                    <Table
                        head={cols ? ['Item', ...cols] : null}
                        rows={cost.rows.map(costRow)}
                        foot={cols ? ['Total', ...cost.totals.map(money)] : ['Total', money(cost.total)]}
                    />
                    {cost.note && <p>{cost.note}</p>}
                    <SourceNote sources={sources} sourceKeyName={cost.source} where={cost.where} />
                    <p className="pt-2">Average net price per year, by family income, {SCORECARD_NET_PRICE_YEAR}:</p>
                    <Table
                        head={['Family income', 'Paid after grants']}
                        rows={cost.netPrice.byIncome.map((r) => [r.income, money(r.amount)])}
                        foot={['All incomes', usd(cost.netPrice.average)]}
                    />
                    <p>{cost.netPrice.note}</p>
                    <SourceNote sources={sources} sourceKeyName={cost.netPrice.source} />
                </FactSection>

                {c.faq && c.faq.questions && c.faq.questions.length > 0 && (
                    <FactSection id="faq" title="Common questions">
                        <div className="space-y-5" data-testid="college-faq">
                            {c.faq.questions.map((f) => (
                                <div key={f.q}>
                                    <h3 className="font-semibold text-[#1A2E1F]">{f.q}</h3>
                                    <p className="mt-1">{f.a}</p>
                                    <SourceNote sources={sources} sourceKeyName={f.source} where={f.where} className="mt-1" />
                                </div>
                            ))}
                        </div>
                    </FactSection>
                )}

                <CollegeVideos college={c} videos={videos} />

                <SimilarSchools college={c} />

                {/* The link names this school, so the free read opens pre-set
                    for it (#2608); every school's page uses the same link. */}
                {/* data-signup-moments: the go-live card's live read-back (#5214,
                    desk_feature_launch LAUNCHABLE["signup_moments"].live_proof).
                    The prerender seeds the public flags, so a build with
                    signup_moments on carries it in this page's HTML. */}
                <aside data-testid="college-odds-cta" className="mt-14 rounded-2xl bg-[#1A4D2E] px-6 sm:px-8 py-10 text-center"
                       {...(momentsHere ? { 'data-signup-moments': 'on' } : {})}>
                    <h2 className="font-serif text-2xl font-semibold text-white">See your odds at {c.shortName}, free</h2>
                    <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/80">
                        A school's rate is everyone's average. The free read works from your own grades,
                        activities and major.
                    </p>
                    <Link
                        to={collegeOddsLink(c.slug)}
                        onClick={(e) => {
                            track(EVENTS.COLLEGE_ODDS_CLICKED, { slug: c.slug });
                            // #5195: with the moments on, the card on this page, not the home page
                            if (momentsHere) { e.preventDefault(); openStand('long_read'); }
                        }}
                        className="mt-6 inline-flex min-h-[44px] items-center rounded-lg bg-[#C05838] px-6 text-sm font-semibold text-white hover:bg-[#D96A42]"
                    >
                        See your odds at {c.shortName}, free
                    </Link>
                </aside>
            </main>
            <FooterV2 />
            {sheet.mounted && askOn && (
                <Suspense fallback={null}>
                    <AstraSheet open={sheet.open} school={askSchool} seeds={seeds} pending={sheet.pending}
                        scope={sheet.scope} onClose={closeSheet} onStart={signIn}
                        onUnavailable={() => { closeAsk(); closeSheet(); }} />
                </Suspense>
            )}
        </div>
    );
};

export default CollegePage;
